import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Descriptions, Input, InputNumber, Modal, Radio, Segmented, Space, Table, Tag, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { formatDuration, predictReason, STUDENT_SECONDS, TEACHER_SECONDS, upperBound, verdictColors, verdictLabels,
  verdictOf } from '~/features/attendance-review/model'
import { listAttendanceReviews, resolveAttendanceReview, type AttendanceReview } from '~/services/attendance-review'

type Status = 'PENDING' | 'RESOLVED'

const time = (value: number | null | undefined) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—')

function VerdictTag({ reason }: { reason: string | null }) {
  const verdict = verdictOf(reason)
  return <Tag color={verdictColors[verdict]}>{verdictLabels[verdict]}</Tag>
}

/** 已确认时长 + 无法确认的时长，一行写清 */
function Seconds({ confirmed, unverified }: { confirmed: number; unverified: number }) {
  return <Space direction="vertical" size={0}>
    <Typography.Text>{formatDuration(confirmed)}</Typography.Text>
    {unverified > 0 && <Typography.Text type="warning">另有 {formatDuration(unverified)} 无法确认</Typography.Text>}
  </Space>
}

function ResolveModal({ review, onClose, onResolved }: { review: AttendanceReview; onClose: () => void; onResolved: () => void }) {
  const { evidence } = review
  const bound = upperBound(evidence)
  const [decision, setDecision] = useState<'RECORDED' | 'VERIFIED'>('RECORDED')
  const [teacher, setTeacher] = useState<number | null>(bound.teacher)
  const [student, setStudent] = useState<number | null>(bound.student)
  const [overlap, setOverlap] = useState<number | null>(bound.overlap)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const verified = decision === 'VERIFIED'
  const overlapMax = Math.min(teacher ?? 0, student ?? 0, evidence.maxOverlapSeconds)
  const durationsValid = !verified || (teacher != null && student != null && overlap != null && overlap <= overlapMax)
  const noteValid = note.trim().length >= 3
  const outcome = verified
    ? (teacher != null && student != null ? predictReason(evidence, teacher, student, overlap) : null)
    : verdictOf(review.candidateReason)

  const submit = async () => {
    setSaving(true)
    try {
      await resolveAttendanceReview(review.appointmentId, verified
        ? { decision, note: note.trim(), teacherSeconds: teacher, studentSeconds: student, overlapSeconds: overlap }
        : { decision, note: note.trim() })
      message.success('已结算')
      onResolved()
    } catch (error) {
      const status = (error as { status?: number }).status
      message.error(status === 409 ? '这节课已被结算或取消，请刷新' : '提交失败，请检查填写后重试')
    } finally {
      setSaving(false)
    }
  }

  return <Modal open title={`处理考勤复核 · 预约 ${review.appointmentId}`} width={640} onCancel={onClose} destroyOnClose
    okText="确认结算" okButtonProps={{ disabled: !noteValid || !durationsValid, loading: saving }} onOk={submit}>
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="学生">{review.studentName ?? '—'}（{review.studentId ?? '—'}）</Descriptions.Item>
        <Descriptions.Item label="老师">{review.teacherName ?? '—'}（{review.teacherId ?? '—'}）</Descriptions.Item>
        <Descriptions.Item label="上课时间" span={2}>{time(review.classStartTime)}</Descriptions.Item>
        <Descriptions.Item label="老师在课"><Seconds confirmed={evidence.teacherSeconds} unverified={evidence.teacherUnverifiedSeconds} /></Descriptions.Item>
        <Descriptions.Item label="学生在课"><Seconds confirmed={evidence.studentSeconds} unverified={evidence.studentUnverifiedSeconds} /></Descriptions.Item>
      </Descriptions>
      <Typography.Text type="secondary">
        门槛：老师至少 {TEACHER_SECONDS / 60} 分钟、学生至少 {STUDENT_SECONDS / 60} 分钟。「无法确认」是心跳中断、又没收到离开事件的时段，可能是断网、App 切后台或被系统关闭。可以对照课堂回放核实。
      </Typography.Text>
      <Radio.Group value={decision} onChange={event => setDecision(event.target.value)}>
        <Space direction="vertical">
          <Radio value="RECORDED">按已确认的时长结算（无法确认的时段不算）</Radio>
          <Radio value="VERIFIED">我已核实，按下面填写的时长结算</Radio>
        </Space>
      </Radio.Group>
      {verified && <Space wrap>
        <InputNumber addonBefore="老师" addonAfter="秒" min={0} max={evidence.maxAttendanceSeconds} precision={0} value={teacher} onChange={setTeacher} />
        <InputNumber addonBefore="学生" addonAfter="秒" min={0} max={evidence.maxAttendanceSeconds} precision={0} value={student} onChange={setStudent} />
        <InputNumber addonBefore="同时在课" addonAfter="秒" min={0} max={overlapMax} precision={0} value={overlap} onChange={setOverlap} />
      </Space>}
      {verified && !durationsValid && <Alert type="error" showIcon message={`同时在课不能超过老师和学生各自的时长（最多 ${overlapMax} 秒）`} />}
      <Alert type="info" showIcon message={<>结算结果{verified ? '（预估）' : ''}：{outcome ? <Tag color={verdictColors[outcome]}>{verdictLabels[outcome]}</Tag> : '—'}</>}
        description={verified ? '预估未考虑「老师开课后才确认预约」的改判，最终以系统结算为准。' : undefined} />
      <Input.TextArea rows={3} maxLength={2000} showCount placeholder="复核说明（必填，至少 3 个字），例如：看过回放，学生 14:06 后一直在线"
        value={note} onChange={event => setNote(event.target.value)} />
    </Space>
  </Modal>
}

export default function AttendanceReviewPage() {
  const [status, setStatus] = useState<Status>('PENDING')
  const [page, setPage] = useState(1)
  const [size] = useState(20)
  const [rows, setRows] = useState<AttendanceReview[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [selected, setSelected] = useState<AttendanceReview | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    try {
      const data = await listAttendanceReviews(status, page, size)
      setRows(data.content)
      setTotal(data.total)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [status, page, size])

  useEffect(() => { fetchData() }, [fetchData])

  const columns: ColumnsType<AttendanceReview> = [
    { title: '上课时间', dataIndex: 'classStartTime', width: 150, render: time },
    { title: '预约 ID', dataIndex: 'appointmentId', width: 90 },
    { title: '学生', width: 140, render: (_, row) => row.studentName ?? row.studentId ?? '—' },
    { title: '老师', width: 140, render: (_, row) => row.teacherName ?? row.teacherId ?? '—' },
    { title: '老师在课', width: 170, render: (_, row) => <Seconds confirmed={row.evidence.teacherSeconds} unverified={row.evidence.teacherUnverifiedSeconds} /> },
    { title: '学生在课', width: 170, render: (_, row) => <Seconds confirmed={row.evidence.studentSeconds} unverified={row.evidence.studentUnverifiedSeconds} /> },
    status === 'PENDING'
      ? { title: '按已确认时长会判为', width: 160, render: (_, row) => <VerdictTag reason={row.candidateReason} /> }
      : { title: '处理方式', width: 200, render: (_, row) => row.resolution && <Space direction="vertical" size={0}>
          <Typography.Text>{row.resolution.decision === 'VERIFIED'
            ? `核实后：老师 ${formatDuration(row.resolution.teacherSeconds)} / 学生 ${formatDuration(row.resolution.studentSeconds)}`
            : '按已确认时长'}</Typography.Text>
          <Typography.Text type="secondary">{row.resolution.note}</Typography.Text>
        </Space> },
    status === 'PENDING'
      ? { title: '进入复核', dataIndex: 'createdAt', width: 150, render: time }
      : { title: '处理时间', dataIndex: 'resolvedAt', width: 150, render: time },
    ...(status === 'PENDING' ? [{ title: '操作', key: 'action', width: 90, fixed: 'right' as const,
      render: (_: unknown, row: AttendanceReview) => <Button type="primary" onClick={() => setSelected(row)}>处理</Button> }] : []),
  ]

  return <Space direction="vertical" size="large" style={{ display: 'flex', padding: 24 }}>
    <div>
      <Typography.Title level={2}>考勤复核</Typography.Title>
      <Typography.Text type="secondary">
        课堂结束时，如果老师或学生有一段在线时间无法确认，而算不算这段时间会得出不同结论，系统不会自动判定缺席，而是放到这里等人工处理。处理前这节课的状态保持「已确认」，会一直出现在学生和老师的「即将到来」里，也不能写课后总结。
      </Typography.Text>
    </div>
    <Space wrap>
      <Segmented<Status> value={status} onChange={value => { setStatus(value); setPage(1) }}
        options={[{ label: '待处理', value: 'PENDING' }, { label: '已处理', value: 'RESOLVED' }]} />
      <Button onClick={fetchData} loading={loading}>刷新</Button>
    </Space>
    {failed ? <Alert type="error" showIcon message="加载考勤复核失败" action={<Button onClick={fetchData}>重试</Button>} />
      : <Table rowKey="appointmentId" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 1200 }}
        locale={{ emptyText: status === 'PENDING' ? '没有待处理的课' : '暂无记录' }}
        pagination={{ current: page, pageSize: size, total, showSizeChanger: false, onChange: setPage }} />}
    {selected && <ResolveModal review={selected} onClose={() => setSelected(null)}
      onResolved={() => { setSelected(null); fetchData() }} />}
  </Space>
}
